import {defineStore} from 'pinia';
import {useAppStateStore} from "@/modules/chat/stores/appStore";
import {
    ASSISTANT_PROGRESS_STATUS, IMAGE_VARIATION,
    MESSAGE_TYPE_ASSISTANT,
    MESSAGE_TYPE_BATCH_PROGRESS,
    MESSAGE_TYPE_DRAFT, MESSAGE_TYPE_ERROR,
    MESSAGE_TYPE_USER, MODE_IMAGE
} from "@/constants";
import {apiService} from '@/services/apiService';
import {scrollElementToBottomAndCenter, scrollToElement} from "@/services/scrollService";
import {isMobileOrTablet} from "@/services/helper";
import {useToastStore} from "@/modules/chat/stores/toast";
import {useInputStore} from "@/modules/chat/stores/userInputStore";
import {devLog} from "@/services/logger";
import {dialogAssistantApiService} from "@/services/api/dialogAssistantApiService";
import {useReferenceStore} from "@/modules/chat/stores/referenceStore";
import {assistantApiService} from "@/services/api/assistantApiService";
import {jobApiService} from "@/services/api/jobApiService";
import {fileApiService} from "@/services/api/fileApiService";
import {createAssistantMessage, createUserMessage} from "@/modules/chat/services/message";

export const useChatStore = defineStore('chat', {
    state: () => ({
        messages: [],
        dialog: null,
        error: null,
        assistants: [],
    }),
    getters: {
        getAllMessages: (state) => state.messages,
    },
    actions: {
        setError(error) {
            this.error = error;
        },
        setMessages(messages) {
            this.messages = messages;
            this.scrollToBottom();
        },
        addUserMessage(message) {
            devLog('addUserMessage');
            this.messages.push(message);
            this.scrollToBottom();
        },
        addDraftMessage(message, scrollBottom = true) {
            devLog('addDraftMessage');
            this.messages.push(message);
            if (scrollBottom) {
                this.scrollToBottom();
            }
        },
        addErrorMessage(message) {
            devLog('addErrorMessage');
            this.messages.push(message);
            this.scrollToBottom();
        },
        addBatchProgressMessage(batchId, assistantIds) {
            devLog('addBatchProgressMessage');
            const reference = useReferenceStore();
            const result = assistantIds.map((id) => {
                const assistant = reference.assistants.find((referenceAssistant) => referenceAssistant.id === id);
                // первому ассистенту ставим статус WORKING, остальные WAITING
                return {...assistant, status: id === assistantIds[0] ? ASSISTANT_PROGRESS_STATUS.WORKING : ASSISTANT_PROGRESS_STATUS.WAITING};
            });
            const message = {id: batchId, type: MESSAGE_TYPE_BATCH_PROGRESS, progress: 0, assistants: result};
            this.messages.push(message);
        },
        updateBatchProgressMessage(batchId, progress) {
            devLog('updateBatchProgressMessage');

            this.messages = this.messages.map((message) =>
                message.id === batchId ? { ...message, progress } : message
            );
        },
        setStatusBatchProgressAssistant(batchId, assistantId, status) {
            devLog('setStatusBatchProgressAssistant');

            const messageIndex = this.messages.findIndex((message) => message.id === batchId);
            if (messageIndex !== -1) {
                const message = this.messages[messageIndex];
                const assistants = message.assistants.map((assistant) => assistant.id === assistantId ? {...assistant, status} : assistant);
                const replacement = {...message, assistants};
                // Заменяем сообщение
                this.messages.splice(messageIndex, 1, replacement);
            }
        },
        // при ошибке ассистента
        setStatusBatchProgressAssistantError(batchId) {
            devLog('setStatusBatchProgressAssistantError');

            const messageIndex = this.messages.findIndex((message) => message.id === batchId);
            if (messageIndex !== -1) {
                const message = this.messages[messageIndex];
                // находим в статусе WORKING и меняем его на ERROR
                const assistants = message.assistants.map((assistant) => assistant.status === ASSISTANT_PROGRESS_STATUS.WORKING ? {...assistant, status: ASSISTANT_PROGRESS_STATUS.ERROR} : assistant);
                const replacement = {...message, assistants};
                // Заменяем сообщение
                this.messages.splice(messageIndex, 1, replacement);
            }
        },
        setStatusBatchProgressAssistantNextProgress(batchId, assistantId) {
            devLog('setStatusBatchProgressAssistantNextProgress');

            const messageIndex = this.messages.findIndex((message) => message.id === batchId);
            if (messageIndex !== -1) {
                const message = this.messages[messageIndex];
                const assistants = message.assistants.map((assistant) => assistant.id === assistantId ? {...assistant, status: ASSISTANT_PROGRESS_STATUS.WORKING} : assistant);
                const replacement = {...message, assistants};
                // Заменяем сообщение
                this.messages.splice(messageIndex, 1, replacement);
            }
        },
        // заменить все cancelled, error на waiting
        setStatusBatchProgressAssistantWaiting(batchId) {
            devLog('setStatusBatchProgressAssistantWaiting');

            const messageIndex = this.messages.findIndex((message) => message.id === batchId);
            if (messageIndex !== -1) {
                const message = this.messages[messageIndex];
                const assistants = message.assistants.map((assistant) => assistant.status === ASSISTANT_PROGRESS_STATUS.CANCELED || assistant.status === ASSISTANT_PROGRESS_STATUS.ERROR ? {...assistant, status: ASSISTANT_PROGRESS_STATUS.WAITING} : assistant);
                const replacement = {...message, assistants};
                // Заменяем сообщение
                this.messages.splice(messageIndex, 1, replacement);
            }
        },
        // заменить все waiting на canceled
        setStatusBatchProgressAssistantCanceled(batchId) {
            devLog('setStatusBatchProgressAssistant');

            const messageIndex = this.messages.findIndex((message) => message.id === batchId);
            if (messageIndex !== -1) {
                const message = this.messages[messageIndex];
                // находим в статусе WAITING и меняем его на CANCELED
                const assistants = message.assistants.map((assistant) => assistant.status === ASSISTANT_PROGRESS_STATUS.WAITING ? {...assistant, status: ASSISTANT_PROGRESS_STATUS.CANCELED} : assistant);
                const replacement = {...message, assistants};
                // Заменяем сообщение
                this.messages.splice(messageIndex, 1, replacement);
            }
        },
        getDraftMessage() {
            return this.messages.find((message) => message.type === MESSAGE_TYPE_DRAFT);
        },
        getBatchProgressMessage(batchId) {
            const messageIndex = this.messages.findIndex((message) => message.id === batchId);
            if (messageIndex !== -1) {
                return this.messages[messageIndex];
            }
            return null;
        },
        removeDraftMessage() {
            this.messages = this.messages.filter((message) => message.type !== MESSAGE_TYPE_DRAFT);
        },
        removeErrorMessage() {
            this.messages = this.messages.filter((message) => message.type !== MESSAGE_TYPE_ERROR);
        },
        replaceDraftMessage(replacement) {
            const messageIndex = this.messages.findIndex((message) => message.type === MESSAGE_TYPE_DRAFT);
            if (messageIndex !== -1) {
                // Заменяем сообщение
                this.messages.splice(messageIndex, 1, replacement);
            }
        },
        replaceMessageType(messageId, replacement) {
            // Находим индекс сообщения, которое нужно заменить
            const messageIndex = this.messages.findIndex((message) => message.id === messageId);
            if (messageIndex !== -1) {
                // Заменяем сообщение
                this.messages.splice(messageIndex, 1, {...this.messages[messageIndex], ...replacement});
            }
        },
        replaceMessage(messageId, replacement) {
            // Находим индекс сообщения, которое нужно заменить
            const messageIndex = this.messages.findIndex((message) => message.id === messageId);
            if (messageIndex !== -1) {
                // Заменяем сообщение
                this.messages.splice(messageIndex, 1, replacement);
            }
        },
        replaceDraftMessageByRegenerationId(regenerationId, replacement) {
            // Находим индекс сообщения, которое нужно заменить
            const messageIndex = this.messages.findIndex((message) => message.regeneration_id === regenerationId);
            if (messageIndex !== -1) {
                // Заменяем сообщение
                this.messages.splice(messageIndex, 1, replacement);
            }
        },
        scrollToBottom() {
            if (isMobileOrTablet()) {
                scrollElementToBottomAndCenter('.chat-widget__footer-send', 20);
            } else {
                scrollToElement('.chat-widget__wrapper');
            }
        },
        updateMessage(id, text) {
            this.messages = this.messages.map((message) =>
                message.id === id ? { ...message, text } : message
            );
        },
        addAssistantMessage(message, scrollBottom = true) {
            devLog('addAssistantMessage');
            this.messages.push(message);
            if (scrollBottom) {
                this.scrollToBottom();
            }
        },
        async createAssistant(data) {
            try {
                const assistant = await assistantApiService.create(data);

                const reference = useReferenceStore();
                reference.assistants.push(assistant);

                const toastStore = useToastStore();
                toastStore.showSecondary(`Ассистент создан`);

                return assistant;
            } catch (error) {
                const errorText = error.message || String(error);

                const toastStore = useToastStore();
                toastStore.showError(`${errorText}`);

                throw error;
            }
        },
        async updateAssistant(assistantId, data) {
            try {
                const assistant = await assistantApiService.update(assistantId, data);

                const reference = useReferenceStore();
                const assistantIndex = reference.assistants.findIndex(a => a.id === assistantId);
                reference.assistants.splice(assistantIndex, 1, assistant);

                const toastStore = useToastStore();
                toastStore.showSecondary(`Ассистент обновлен`);

                return assistant;
            } catch (error) {
                const errorText = error.message || String(error);

                const toastStore = useToastStore();
                toastStore.showError(`${errorText}`);

                throw error;
            }
        },
        async deleteFile(fileId) {
            try {
                await fileApiService.delete(fileId);

                // удалить файл из файлов сообщения

                const toastStore = useToastStore();
                toastStore.showSecondary(`Файл удален`);
            } catch (error) {
                const errorText = error.message || String(error);
                if (error.disableAll) {
                    this.setError(errorText);

                    const appStateStore = useAppStateStore();
                    appStateStore.showLoading = false;
                    appStateStore.initial = false;

                    appStateStore.setDisableAll(error.disableAll);
                } else {
                    const toastStore = useToastStore();
                    toastStore.showError(`${errorText}`);

                    throw error;
                }
            }
        },
        async deleteAssistant(assistantId) {
            try {
                await assistantApiService.delete(assistantId);

                this.assistants = this.assistants.filter(a => a.assistant_id !== assistantId);

                const reference = useReferenceStore();
                const assistantIndex = reference.assistants.findIndex(a => a.id === assistantId);
                reference.assistants.splice(assistantIndex, 1);

                const toastStore = useToastStore();
                toastStore.showSecondary(`Ассистент удален`);
            } catch (error) {
                const errorText = error.message || String(error);

                const toastStore = useToastStore();
                toastStore.showError(`${errorText}`);

                throw error;
            }
        },
        async attachAssistant(assistantId) {
            if (this.dialog) {
                try {
                    await dialogAssistantApiService.attach(this.dialog.id, assistantId);

                    const reference = useReferenceStore();
                    const assistant = reference.assistants.find(a => a.id === assistantId);
                    this.assistants.push({assistant_id: assistantId, order: this.assistants.length, assistant});

                    const toastStore = useToastStore();
                    toastStore.showSecondary(`Ассистент добавлен в чат`);
                } catch (error) {
                    const errorText = error.message || String(error);

                    const toastStore = useToastStore();
                    toastStore.showError(`${errorText}`);

                    throw error;
                }
            } else {
                const reference = useReferenceStore();
                const assistant = reference.assistants.find(a => a.id === assistantId);
                this.assistants.push({assistant_id: assistantId, order: this.assistants.length, assistant});
            }
        },
        async updateAssistantsOrder(assistants) {
            if (this.dialog) {
                const oldOrder = this.assistants;
                try {
                    this.assistants = assistants;
                    const assistantIds = assistants.map(dialogAssistant => dialogAssistant.assistant_id);

                    await dialogAssistantApiService.syncOrder(this.dialog.id, assistantIds);
                } catch (error) {
                    this.assistants = oldOrder;

                    const errorText = error.message || String(error);

                    const toastStore = useToastStore();
                    toastStore.showError(`${errorText}`);

                    throw error;
                }
            } else {
                this.assistants = assistants;
            }
        },
        async detachAssistant(assistantId) {
            if (this.dialog) {
                try {
                    await dialogAssistantApiService.detach(this.dialog.id, assistantId);

                    // удалить из dialogAssistant
                    this.assistants = this.assistants.filter(a => a.assistant_id !== assistantId);

                    const toastStore = useToastStore();
                    toastStore.showSecondary(`Ассистент удален из чата`);
                } catch (error) {
                    const errorText = error.message || String(error);

                    const toastStore = useToastStore();
                    toastStore.showError(`${errorText}`);

                    throw error;
                }
            } else {
                this.assistants = this.assistants.filter(a => a.assistant_id !== assistantId);
            }
        },
        async removeMessage(id) {
            try {
                await apiService.removeMessage(id);
                this.messages = this.messages.filter((message) => message.id !== id);
            } catch (error) {
                const errorText = error.message || String(error);

                const toastStore = useToastStore();
                toastStore.showError(`${errorText}`);

                throw error;
            }
        },
        async updateMessageText(id, text) {
            try {
                await apiService.updateMessageText(id, text);
                this.updateMessage(id, text);
            } catch (error) {
                const errorText = error.message || String(error);

                const toastStore = useToastStore();
                toastStore.showError(`${errorText}`);

                throw error;
            }
        },
        async fetchMessages(dialogHash) {
            try {
                const messages = await apiService.fetchMessages(dialogHash);
                const messageResult = messages.map((message) => {
                    const {id, text, type, created_at: createdAt, reason, stat, files, images, uml_images: uml, can_regenerate: canRegenerate, search, assistant, music_files: musicFiles, web_search: webSearch} = message;
                    return type === MESSAGE_TYPE_ASSISTANT
                        ? createAssistantMessage(id, text, createdAt, reason, stat, files, images, uml, canRegenerate, search, assistant, musicFiles, webSearch)
                        : createUserMessage(id, text, createdAt, files);
                });
                this.setMessages(messageResult);
            } catch (error) {
                const errorText = error.message || String(error);

                const toastStore = useToastStore();
                toastStore.showError(`${errorText}`);

                throw error;
            }
        },
        async fetchDialog(id) {
            try {
                this.dialog = await apiService.getDialog(id);
                this.assistants = this.dialog.assistants;
                useInputStore().webSearch = this.dialog.web_search;
            } catch (error) {
                const errorText = error.message || String(error);
                if (error.disableAll) {
                    this.setError(errorText);

                    const appStateStore = useAppStateStore();
                    appStateStore.showLoading = false;
                    appStateStore.initial = false;

                    appStateStore.setDisableAll(error.disableAll);
                } else {
                    const toastStore = useToastStore();
                    toastStore.showError(`${errorText}`);

                    throw error;
                }
            }
        },
        async createDialog(query, mode, model, systemPrompt = null, assistantsIds = null, webSearch = null) {
            try {
                this.dialog = await apiService.createDialog(query, mode, model, systemPrompt, assistantsIds, webSearch);
                return this.dialog;
            } catch (error) {
                const errorText = error.message || String(error);

                if (error.disableAll) {
                    this.setError(error.message || String(error));

                    const appStateStore = useAppStateStore();
                    appStateStore.setDisableAll(error.disableAll);
                } else if (error.validationError) {
                    this.setError(error.message || String(error));
                } else {
                    const toastStore = useToastStore();
                    toastStore.showError(`${errorText}`);

                    throw error;
                }
            }
        },
        async imageVariation(messageId, url) {
            try {
                const appStateStore = useAppStateStore();

                appStateStore.startBlockSending();
                this.setError(null);
                await apiService.imageVariation(messageId, url);
            } catch (error) {
                const errorText = error.message || String(error);

                if (error.disableAll) {
                    this.setError(error.message || String(error));

                    const appStateStore = useAppStateStore();
                    appStateStore.setDisableAll(error.disableAll);
                } else if (error.validationError) {
                    this.setError(error.message || String(error));
                } else {
                    const toastStore = useToastStore();
                    toastStore.showError(`${errorText}`);

                    throw error;
                }
            }
        },
        async patchDialog(dialogHash, data) {
            try {
                return await apiService.patchDialog(dialogHash, data);
            } catch (error) {
                const errorText = error.message || String(error);
                const toastStore = useToastStore();
                toastStore.showError(`${errorText}`);

                return false;
            }
        },
        async modelChangeHandle()  {
            const {selectedModel} = useInputStore();
            const appStateStore = useAppStateStore();
            await appStateStore.setShowSearchDomainFilter(selectedModel.online);
            await appStateStore.setShowMusicFields(selectedModel.music);
            const canImageVariation = selectedModel.image_features.includes(IMAGE_VARIATION);
            await appStateStore.setImageVariationFeature(canImageVariation);

            if (appStateStore.mode === MODE_IMAGE) {
                await appStateStore.setAttachFiles(canImageVariation);
                await appStateStore.setLimitFiles(1);
            } else {
                await appStateStore.setLimitFiles(0);
            }

            if (this.dialog) {
                const {id} = this.dialog;

                if (selectedModel) {
                    const updateData = {model: selectedModel.id};
                    if (useInputStore().webSearch && !selectedModel.web_search) {
                        updateData.web_search = false;

                        useInputStore().webSearch = false;
                    }

                    await this.patchDialog(id, updateData);
                }
            } else if (selectedModel) {
                if (useInputStore().webSearch && !selectedModel.web_search) {
                    useInputStore().webSearch = false;
                }
            }
        },
        async regenerateMessage(messageId) {
            try {
                return await apiService.regenerateMessage(messageId);
            } catch (error) {
                this.setError(error.message || String(error));

                const appStateStore = useAppStateStore();
                appStateStore.stopBlockSending();
                this.removeDraftMessage();

                appStateStore.setDisableAll(error.disableAll ?? false);

                return false;
            }
        },
        async resendUserMessage(messageId) {
            try {
                return await apiService.resendUserMessage(messageId);
            } catch (error) {
                this.setError(error.message || String(error));

                const appStateStore = useAppStateStore();
                appStateStore.stopBlockSending();
                this.removeDraftMessage();

                appStateStore.setDisableAll(error.disableAll ?? false);

                return false;
            }
        },
        async retryJob(jobId) {
            try {
                return await jobApiService.retry(jobId);
            } catch (error) {
                this.setError(error.message || String(error));

                const appStateStore = useAppStateStore();
                appStateStore.stopBlockSending();
                this.removeDraftMessage();

                appStateStore.setDisableAll(error.disableAll ?? false);

                return false;
            }
        },
        async retryBatchJob(batchJobId) {
            try {
                return await jobApiService.retryBatchJob(batchJobId);
            } catch (error) {
                this.setError(error.message || String(error));

                const appStateStore = useAppStateStore();
                appStateStore.stopBlockSending();
                this.removeDraftMessage();

                appStateStore.setDisableAll(error.disableAll ?? false);

                return false;
            }
        },
        async sendMessage(query, dialogHash, files = null, uml = null, searchDomainFilter = null, musicTitle = null, musicTags = null) {
            try {
                return await apiService.sendMessage(query, MESSAGE_TYPE_USER, dialogHash, files, uml, searchDomainFilter, musicTitle, musicTags);
            } catch (error) {
                this.setError(error.message || String(error));

                const appStateStore = useAppStateStore();
                appStateStore.stopBlockSending();

                appStateStore.setDisableAll(error.disableAll ?? false);

                return false;
            }
        },
    },
});